import { memo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AuthForm from 'components/auth/tools/AuthForm';
import EmailConfirmationModal from 'components/modals/EmailConfirmationModal';
import NiceModal from 'components/modals/NiceModal';
import SubscriptionModal from 'components/modals/SubscriptionModal';
import ScenarioGeneratorCard from 'components/ScenarioGeneratorCard';
import useModalState from 'hooks/useModalState';
import { useMappedState } from 'redux-react-hook';
import { GUEST_ROUTES } from 'routes/routes';
import styled from 'styled-components';
import { errorAlert } from 'ui-kit/Alert';
import Spinner from 'ui-kit/Spinner';

import UserTemplate from '../../components/templates/UserTemplate/UserTemplate';
import { useGetChatByNameQuery } from '../../services/api/chatsApi';
import {
  AdvantagesSection as ChatAdvantagesSection,
  FAQSection as ChatFAQSection,
  HeadingSection as ChatHeadingSection,
  HeroSection as ChatHeroSection,
  HowItWorksSection as ChatHowItWorksSection,
  ScenarioGeneratorSection as ChatScenarioGeneratorSection,
  ScenariosSection as ChatScenariosSection,
  SignUpBlockSection as ChatSignUpBlockSection,
  TryOurChatsSection as ChatTryOurChatsSection
} from './components';

const ChatPage = () => {
  const { chatName } = useParams<{ chatName: string }>();

  const [isOpenAuthModal, openAuthModal, closeAuthModal] = useModalState();
  const [isSubscriptionModalOpen, openSubscriptionModal, closeSubscriptionModal] = useModalState();
  const [isOpenEmailConfirmationModal, openEmailConfirmationModal, closeEmailConfirmationModal] =
    useModalState();
  const navigate = useNavigate();

  const {
    data: chat,
    error,
    isLoading,
  } = useGetChatByNameQuery(chatName || '', {
    skip: !chatName,
  });


  const { currentUser } = useMappedState(state => {
    const currentUser = state.users.usersMap.get(state.users.currentUserId);
    const hasSubscription = currentUser?.hasSubscription;

    return {
      currentUser: state.users.usersMap.get(state.users.currentUserId),
      hasSubscription: hasSubscription,
      isSignedIn: state.users.currentUserId
    };
  });

  const handleCreateScenarioButtonClick = () => {
    if (!currentUser) {
      openAuthModal();
    } else if (!currentUser?.emailVerified) {
      openEmailConfirmationModal();
    } else {
      navigate(GUEST_ROUTES.SCENARIOS_GENERATOR());
    }
  };


  useEffect(() => {
    if (error) {
      errorAlert("Chat not found");
      navigate(GUEST_ROUTES.ROOT());
    }
  }, [error, navigate]);

  if (isLoading) {
    return (
      <UserTemplate>
        <Spinner />
      </UserTemplate>
    );
  }

  if (!chat) {
    return null;
  }

  return (
    <UserTemplate>
      <ChatContainer>
        <title>{chat.title}</title>
        <meta name="description" content={chat.hero?.description || ''} />

        {chat.hero && (
          <ChatHeroSection title={chat.title || chat.name} hero={chat.hero} />
        )}

        {chat.heading && chat.heading.length > 0 && (
          <ChatHeadingSection heading={chat.heading} />
        )}

        {chat.scenarioGenerator && (
          <ChatScenarioGeneratorSection scenarioGenerator={chat.scenarioGenerator} />
        )}

        <ScenarioGeneratorCard onButtonClick={handleCreateScenarioButtonClick} />

        {chat.scenarios && chat.scenarios.length > 0 && (
          <ChatScenariosSection scenarios={chat.scenarios} onOpenAuthModal={openAuthModal} onOpenSubscriptionModal={openSubscriptionModal} />
        )}

        {chat.signUpBlock && !currentUser && (
          <ChatSignUpBlockSection signUpBlock={chat.signUpBlock} onOpenAuthModal={openAuthModal} />
        )}

        {chat.advantages && chat.advantages.length > 0 && (
          <ChatAdvantagesSection advantages={chat.advantages} />
        )}

        {chat.howItWorks && chat.howItWorks.length > 0 && (
          <ChatHowItWorksSection howItWorks={chat.howItWorks} />
        )}

        {chat.chats && chat.chats.length > 0 && (
          <ChatTryOurChatsSection chats={chat.chats} />
        )}

        {chat.faq && chat.faq.length > 0 && (
          <ChatFAQSection faq={chat.faq} />
        )}
      </ChatContainer>
      {isOpenAuthModal && (
        <NiceModal onClose={closeAuthModal}>
          <ModalInnerPlaceholder>
            <AuthForm onClose={closeAuthModal} />
          </ModalInnerPlaceholder>
        </NiceModal>
      )}
      {isSubscriptionModalOpen && (
        <SubscriptionModal
          analyticsData={{
            rendered_for_reason: 'premium_roleplay_click_from_chat_page'
          }}
          isOpen
          onClose={closeSubscriptionModal}
        />
      )}
      <EmailConfirmationModal
        isOpen={isOpenEmailConfirmationModal}
        onClose={closeEmailConfirmationModal}
      />
    </UserTemplate>
  );
};

const ChatContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  
  @media (max-width: 968px) {
    padding: 0;
  }
`;

const ModalInnerPlaceholder = styled.div`
  width: 380px;
  padding: 30px;
  box-sizing: border-box;
  @media (max-width: 400px) {
    width: 100%;
  }
`;

export default memo(ChatPage); 